// resources.js - Redux reducer for Resources page

export const RESOURCES_ACTIONS = {
	FETCH_REQUEST: 'RESOURCES_FETCH_REQUEST',
	FETCH_SUCCESS: 'RESOURCES_FETCH_SUCCESS',
	FETCH_FAILURE: 'RESOURCES_FETCH_FAILURE',
	ADD_REQUEST: 'RESOURCES_ADD_REQUEST',
	ADD_SUCCESS: 'RESOURCES_ADD_SUCCESS',
	ADD_FAILURE: 'RESOURCES_ADD_FAILURE',
	UPDATE_REQUEST: 'RESOURCES_UPDATE_REQUEST',
	UPDATE_SUCCESS: 'RESOURCES_UPDATE_SUCCESS',
	UPDATE_FAILURE: 'RESOURCES_UPDATE_FAILURE',
	DELETE_REQUEST: 'RESOURCES_DELETE_REQUEST',
	DELETE_SUCCESS: 'RESOURCES_DELETE_SUCCESS',
	DELETE_FAILURE: 'RESOURCES_DELETE_FAILURE',
	TOGGLE_VISIBILITY_OPTIMISTIC: 'RESOURCES_TOGGLE_VISIBILITY_OPTIMISTIC',
	TOGGLE_VISIBILITY_SUCCESS: 'RESOURCES_TOGGLE_VISIBILITY_SUCCESS',
	TOGGLE_VISIBILITY_ROLLBACK: 'RESOURCES_TOGGLE_VISIBILITY_ROLLBACK',
	BLOCK_TYPE_OPTIMISTIC: 'RESOURCES_BLOCK_TYPE_OPTIMISTIC',
	BLOCK_TYPE_SUCCESS: 'RESOURCES_BLOCK_TYPE_SUCCESS',
	BLOCK_TYPE_ROLLBACK: 'RESOURCES_BLOCK_TYPE_ROLLBACK',
	UNBLOCK_TYPE_OPTIMISTIC: 'RESOURCES_UNBLOCK_TYPE_OPTIMISTIC',
	UNBLOCK_TYPE_SUCCESS: 'RESOURCES_UNBLOCK_TYPE_SUCCESS',
	UNBLOCK_TYPE_ROLLBACK: 'RESOURCES_UNBLOCK_TYPE_ROLLBACK',
	UPDATE_TYPE_NAME_OPTIMISTIC: 'RESOURCES_UPDATE_TYPE_NAME_OPTIMISTIC',
	UPDATE_TYPE_NAME_SUCCESS: 'RESOURCES_UPDATE_TYPE_NAME_SUCCESS',
	UPDATE_TYPE_NAME_ROLLBACK: 'RESOURCES_UPDATE_TYPE_NAME_ROLLBACK',
	// Maintenance actions
	FETCH_MAINTENANCE_REQUEST: 'RESOURCES_FETCH_MAINTENANCE_REQUEST',
	FETCH_MAINTENANCE_SUCCESS: 'RESOURCES_FETCH_MAINTENANCE_SUCCESS',
	FETCH_MAINTENANCE_FAILURE: 'RESOURCES_FETCH_MAINTENANCE_FAILURE',
	ADD_MAINTENANCE_OPTIMISTIC: 'RESOURCES_ADD_MAINTENANCE_OPTIMISTIC',
	ADD_MAINTENANCE_SUCCESS: 'RESOURCES_ADD_MAINTENANCE_SUCCESS',
	ADD_MAINTENANCE_ROLLBACK: 'RESOURCES_ADD_MAINTENANCE_ROLLBACK',
	UPDATE_MAINTENANCE_OPTIMISTIC: 'RESOURCES_UPDATE_MAINTENANCE_OPTIMISTIC',
	UPDATE_MAINTENANCE_SUCCESS: 'RESOURCES_UPDATE_MAINTENANCE_SUCCESS',
	UPDATE_MAINTENANCE_ROLLBACK: 'RESOURCES_UPDATE_MAINTENANCE_ROLLBACK',
	DELETE_MAINTENANCE_OPTIMISTIC: 'RESOURCES_DELETE_MAINTENANCE_OPTIMISTIC',
	DELETE_MAINTENANCE_SUCCESS: 'RESOURCES_DELETE_MAINTENANCE_SUCCESS',
	DELETE_MAINTENANCE_ROLLBACK: 'RESOURCES_DELETE_MAINTENANCE_ROLLBACK'
};

const initialState = {
	resources: [],
	resourcesTypes: [],
	locations: [],
	payStatuses: [],
	organizationResourcesTypes: {},
	users: [],
	aircraft: [],
	simulators: [],
	// Aircraft form data
	pilotRatings: [],
	instructorRatings: [],
	medicalClass: [],
	loading: false,
	saving: false,
	error: null,
	// Maintenance state - keyed by resourceId
	maintenance: {},
	maintenanceLoading: false,
	maintenanceError: null
};

export default function resourcesReducer(state = initialState, action) {
	switch (action.type) {
		case RESOURCES_ACTIONS.FETCH_REQUEST:
			return { ...state, loading: true, error: null };

		case RESOURCES_ACTIONS.FETCH_SUCCESS: {
			const data = action.payload;
			return {
				...state,
				loading: false,
				resources: data.resources || state.resources,
				resourcesTypes: data.resourcesTypes || state.resourcesTypes,
				locations: data.locations || state.locations,
				payStatuses: data.payStatuses || state.payStatuses,
				organizationResourcesTypes:
					data.organizationResourcesTypes || state.organizationResourcesTypes,
				users: data.users || state.users,
				aircraft: data.aircraft || state.aircraft,
				simulators: data.simulators || state.simulators,
				// Aircraft form data
				pilotRatings: data.pilotRatings || state.pilotRatings,
				instructorRatings: data.instructorRatings || state.instructorRatings,
				medicalClass: data.medicalClass || state.medicalClass
			};
		}

		case RESOURCES_ACTIONS.FETCH_FAILURE:
			return { ...state, loading: false, error: action.payload.error };

		case RESOURCES_ACTIONS.ADD_REQUEST:
			return { ...state, saving: true, error: null };

		case RESOURCES_ACTIONS.ADD_SUCCESS: {
			const newResource = action.payload.resource;
			let updatedResourcesTypes = state.resourcesTypes;

			// Check if this is a new type that doesn't exist in our list
			// API may return typeId (camelCase) or typeID (PascalCase)
			const typeId = newResource.typeId || newResource.typeID;
			const typeName = newResource.typeName;
			const typeUnique = newResource.typeUnique;

			if (typeId && !state.resourcesTypes.find(t => t.id === typeId)) {
				updatedResourcesTypes = [
					...state.resourcesTypes,
					{
						id: typeId,
						name: typeName,
						unique: typeUnique
					}
				];
			}

			return {
				...state,
				saving: false,
				resources: [...state.resources, newResource],
				resourcesTypes: updatedResourcesTypes
			};
		}

		case RESOURCES_ACTIONS.ADD_FAILURE:
			return { ...state, saving: false, error: action.payload.error };

		case RESOURCES_ACTIONS.UPDATE_REQUEST:
			return { ...state, saving: true, error: null };

		case RESOURCES_ACTIONS.UPDATE_SUCCESS:
			return {
				...state,
				saving: false,
				resources: state.resources.map(r =>
					r.id === action.payload.resource.id ? action.payload.resource : r
				)
			};

		case RESOURCES_ACTIONS.UPDATE_FAILURE:
			return { ...state, saving: false, error: action.payload.error };

		case RESOURCES_ACTIONS.DELETE_REQUEST:
			return { ...state, saving: true, error: null };

		case RESOURCES_ACTIONS.DELETE_SUCCESS:
			return {
				...state,
				saving: false,
				resources: state.resources.filter(r => r.id !== action.payload.resourceId)
			};

		case RESOURCES_ACTIONS.DELETE_FAILURE:
			return { ...state, saving: false, error: action.payload.error };

		case RESOURCES_ACTIONS.TOGGLE_VISIBILITY_OPTIMISTIC:
		case RESOURCES_ACTIONS.TOGGLE_VISIBILITY_ROLLBACK:
			return {
				...state,
				resources: state.resources.map(r =>
					r.id === action.payload.resourceId ? { ...r, published: action.payload.published } : r
				)
			};

		case RESOURCES_ACTIONS.TOGGLE_VISIBILITY_SUCCESS:
			// Already handled by optimistic update
			return state;

		case RESOURCES_ACTIONS.BLOCK_TYPE_OPTIMISTIC: {
			const { typeId, blockedType } = action.payload;
			const typeIdStr = String(typeId);
			const blockedTypeIdStr = String(blockedType.id);

			// Find source type info for bidirectional blocking
			const sourceType = state.resourcesTypes.find(t => String(t.id) === typeIdStr);

			// Update resourcesTypes[].disallowedTypes (bidirectional)
			const updatedResourcesTypes = state.resourcesTypes.map(t => {
				const tIdStr = String(t.id);
				if (tIdStr === typeIdStr) {
					// Add blockedType to this type's disallowedTypes
					const currentDisallowed = t.disallowedTypes || [];
					return {
						...t,
						disallowedTypes: [...currentDisallowed, { id: blockedType.id, name: blockedType.name }]
					};
				}
				if (tIdStr === blockedTypeIdStr && sourceType) {
					// Bidirectional: add sourceType to blockedType's disallowedTypes
					const currentDisallowed = t.disallowedTypes || [];
					return {
						...t,
						disallowedTypes: [...currentDisallowed, { id: sourceType.id, name: sourceType.name }]
					};
				}
				return t;
			});

			return {
				...state,
				resourcesTypes: updatedResourcesTypes
			};
		}

		case RESOURCES_ACTIONS.BLOCK_TYPE_SUCCESS:
			// Already handled by optimistic update
			return state;

		case RESOURCES_ACTIONS.BLOCK_TYPE_ROLLBACK: {
			const { typeId, blockedTypeId, previousResourcesTypes } = action.payload;
			return {
				...state,
				resourcesTypes: previousResourcesTypes || state.resourcesTypes
			};
		}

		case RESOURCES_ACTIONS.UNBLOCK_TYPE_OPTIMISTIC: {
			const { typeId, blockedTypeId } = action.payload;
			const typeIdStr = String(typeId);
			const blockedTypeIdStr = String(blockedTypeId);

			// Update resourcesTypes[].disallowedTypes (bidirectional removal)
			const updatedResourcesTypes = state.resourcesTypes.map(t => {
				const tIdStr = String(t.id);
				if (tIdStr === typeIdStr && t.disallowedTypes) {
					// Remove blockedTypeId from this type's disallowedTypes
					return {
						...t,
						disallowedTypes: t.disallowedTypes.filter(d => String(d.id) !== blockedTypeIdStr)
					};
				}
				if (tIdStr === blockedTypeIdStr && t.disallowedTypes) {
					// Bidirectional: remove typeId from blockedType's disallowedTypes
					return {
						...t,
						disallowedTypes: t.disallowedTypes.filter(d => String(d.id) !== typeIdStr)
					};
				}
				return t;
			});

			return {
				...state,
				resourcesTypes: updatedResourcesTypes
			};
		}

		case RESOURCES_ACTIONS.UNBLOCK_TYPE_SUCCESS:
			// Already handled by optimistic update
			return state;

		case RESOURCES_ACTIONS.UNBLOCK_TYPE_ROLLBACK: {
			const { previousResourcesTypes } = action.payload;
			return {
				...state,
				resourcesTypes: previousResourcesTypes || state.resourcesTypes
			};
		}

		case RESOURCES_ACTIONS.UPDATE_TYPE_NAME_OPTIMISTIC:
		case RESOURCES_ACTIONS.UPDATE_TYPE_NAME_ROLLBACK: {
			const { typeId, newName, oldName } = action.payload;
			const nameToUse =
				action.type === RESOURCES_ACTIONS.UPDATE_TYPE_NAME_OPTIMISTIC ? newName : oldName;

			// Update resourcesTypes
			const updatedResourcesTypes = state.resourcesTypes.map(t =>
				String(t.id) === String(typeId) ? { ...t, name: nameToUse } : t
			);

			// Update typeName in resources (handle both SSR format typeID and API format type)
			const updatedResources = state.resources.map(r => {
				const resourceTypeId =
					r.typeId || r.typeID || (typeof r.type === 'object' ? r.type?.id : r.type);
				if (String(resourceTypeId) === String(typeId)) {
					// Update typeName and also type.name if type is an object
					const updated = { ...r, typeName: nameToUse };
					if (r.type && typeof r.type === 'object') {
						updated.type = { ...r.type, name: nameToUse };
					}
					return updated;
				}
				return r;
			});

			// Update name in organizationResourcesTypes (blocked types references)
			const updatedOrgResourcesTypes = {};
			Object.keys(state.organizationResourcesTypes).forEach(key => {
				updatedOrgResourcesTypes[key] = state.organizationResourcesTypes[key].map(t =>
					String(t.id) === String(typeId) ? { ...t, name: nameToUse } : t
				);
			});

			return {
				...state,
				resourcesTypes: updatedResourcesTypes,
				resources: updatedResources,
				organizationResourcesTypes: updatedOrgResourcesTypes
			};
		}

		case RESOURCES_ACTIONS.UPDATE_TYPE_NAME_SUCCESS:
			// Already handled by optimistic update
			return state;

		// ============ Maintenance Actions ============

		case RESOURCES_ACTIONS.FETCH_MAINTENANCE_REQUEST:
			return { ...state, maintenanceLoading: true, maintenanceError: null };

		case RESOURCES_ACTIONS.FETCH_MAINTENANCE_SUCCESS: {
			const { resourceId, events, defaultEvent, enforceComments } = action.payload;
			return {
				...state,
				maintenanceLoading: false,
				maintenance: {
					...state.maintenance,
					[resourceId]: { events: events || [], defaultEvent, enforceComments }
				}
			};
		}

		case RESOURCES_ACTIONS.FETCH_MAINTENANCE_FAILURE:
			return { ...state, maintenanceLoading: false, maintenanceError: action.payload.error };

		case RESOURCES_ACTIONS.ADD_MAINTENANCE_OPTIMISTIC: {
			const { resourceId, tempId, maintenanceEvent } = action.payload;
			const currentMaintenance = state.maintenance[resourceId] || { events: [] };
			return {
				...state,
				maintenance: {
					...state.maintenance,
					[resourceId]: {
						...currentMaintenance,
						events: [...currentMaintenance.events, { ...maintenanceEvent, tempId }]
					}
				}
			};
		}

		case RESOURCES_ACTIONS.ADD_MAINTENANCE_SUCCESS: {
			const { resourceId, tempId, maintenanceEvent } = action.payload;
			const currentMaintenance = state.maintenance[resourceId] || { events: [] };
			return {
				...state,
				maintenance: {
					...state.maintenance,
					[resourceId]: {
						...currentMaintenance,
						events: currentMaintenance.events.map(e =>
							e.tempId === tempId ? { ...maintenanceEvent, tempId: undefined } : e
						)
					}
				}
			};
		}

		case RESOURCES_ACTIONS.ADD_MAINTENANCE_ROLLBACK: {
			const { resourceId, tempId } = action.payload;
			const currentMaintenance = state.maintenance[resourceId] || { events: [] };
			return {
				...state,
				maintenance: {
					...state.maintenance,
					[resourceId]: {
						...currentMaintenance,
						events: currentMaintenance.events.filter(e => e.tempId !== tempId)
					}
				}
			};
		}

		case RESOURCES_ACTIONS.UPDATE_MAINTENANCE_OPTIMISTIC: {
			const { resourceId, maintenanceId, maintenanceEvent, previousEvent } = action.payload;
			const currentMaintenance = state.maintenance[resourceId] || { events: [] };
			return {
				...state,
				maintenance: {
					...state.maintenance,
					[resourceId]: {
						...currentMaintenance,
						events: currentMaintenance.events.map(e =>
							(e.eventId || e.eventID || e.id) === maintenanceId
								? { ...e, ...maintenanceEvent, _previousEvent: previousEvent }
								: e
						)
					}
				}
			};
		}

		case RESOURCES_ACTIONS.UPDATE_MAINTENANCE_SUCCESS: {
			const { resourceId, maintenanceId } = action.payload;
			const currentMaintenance = state.maintenance[resourceId] || { events: [] };
			return {
				...state,
				maintenance: {
					...state.maintenance,
					[resourceId]: {
						...currentMaintenance,
						events: currentMaintenance.events.map(e =>
							(e.eventId || e.eventID || e.id) === maintenanceId
								? { ...e, _previousEvent: undefined }
								: e
						)
					}
				}
			};
		}

		case RESOURCES_ACTIONS.UPDATE_MAINTENANCE_ROLLBACK: {
			const { resourceId, maintenanceId } = action.payload;
			const currentMaintenance = state.maintenance[resourceId] || { events: [] };
			return {
				...state,
				maintenance: {
					...state.maintenance,
					[resourceId]: {
						...currentMaintenance,
						events: currentMaintenance.events.map(e => {
							if ((e.eventId || e.eventID || e.id) === maintenanceId && e._previousEvent) {
								return e._previousEvent;
							}
							return e;
						})
					}
				}
			};
		}

		case RESOURCES_ACTIONS.DELETE_MAINTENANCE_OPTIMISTIC: {
			const { resourceId, maintenanceId, previousEvent } = action.payload;
			const currentMaintenance = state.maintenance[resourceId] || { events: [] };
			return {
				...state,
				maintenance: {
					...state.maintenance,
					[resourceId]: {
						...currentMaintenance,
						events: currentMaintenance.events.filter(
							e => (e.eventId || e.eventID || e.id) !== maintenanceId
						),
						_deletedEvent: previousEvent
					}
				}
			};
		}

		case RESOURCES_ACTIONS.DELETE_MAINTENANCE_SUCCESS: {
			const { resourceId } = action.payload;
			const currentMaintenance = state.maintenance[resourceId] || { events: [] };
			return {
				...state,
				maintenance: {
					...state.maintenance,
					[resourceId]: {
						...currentMaintenance,
						_deletedEvent: undefined
					}
				}
			};
		}

		case RESOURCES_ACTIONS.DELETE_MAINTENANCE_ROLLBACK: {
			const { resourceId } = action.payload;
			const currentMaintenance = state.maintenance[resourceId] || { events: [] };
			if (currentMaintenance._deletedEvent) {
				return {
					...state,
					maintenance: {
						...state.maintenance,
						[resourceId]: {
							...currentMaintenance,
							events: [...currentMaintenance.events, currentMaintenance._deletedEvent],
							_deletedEvent: undefined
						}
					}
				};
			}
			return state;
		}

		default:
			return state;
	}
}
